import React, { useState, useMemo, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, Spinner } from "reactstrap";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import {
  useScanSampleCollectionTotalAttemptsHistoryService,
  useScanSampleSeperationTotalAttemptsHistoryService,
} from "modules/ScanningApp/api/useScanningSession";
import { getAttemptsModalColumns } from "../helper/attemptsConstant";

export interface TotalAttemptsHistoryModalProps {
  isOpen: boolean;
  toggle: () => void;
  type: "collection" | "separation";
  sessionid: number;
  subjectbarcode?: string;
  vaccutainerbarcode?: string;
  aliquotbarcode?: string;
  subjectno?: string;
}

const TotalAttemptsHistoryModal: React.FC<TotalAttemptsHistoryModalProps> = ({
  isOpen,
  toggle,
  type,
  sessionid,
  subjectbarcode,
  vaccutainerbarcode,
  aliquotbarcode,
  subjectno,
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 5;

  // Collection attempts query hook
  const { SelectAllQuery: collectionQuery } =
    useScanSampleCollectionTotalAttemptsHistoryService({
      sessionid,
      subjectbarcode,
      currentpage: currentPage,
      pagesize: pageSize,
      enabled: isOpen && type === "collection" && !!sessionid && !!subjectbarcode,
    });

  // Separation attempts query hook
  const { SelectAllQuery: separationQuery } =
    useScanSampleSeperationTotalAttemptsHistoryService({
      sessionid,
      vaccutainerbarcode,
      currentpage: currentPage,
      pagesize: pageSize,
      enabled:
        isOpen &&
        type === "separation" &&
        !!sessionid &&
        !!vaccutainerbarcode,
    });

  const query = type === "collection" ? collectionQuery : separationQuery;

  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      query.refetch?.();
    }
  }, [isOpen]);

  const historyData = query?.data?.data || [];
  const isLoading = query.isFetching;

  const totalCount =
    historyData.length > 0 && typeof historyData[0]?.total_count === "number"
      ? historyData[0].total_count
      : historyData.length;

  const columns = useMemo(() => getAttemptsModalColumns(type), [type]);

  const modalTitle =
    type === "collection"
      ? "Sample Collection Attempts History"
      : "Sample Separation Attempts History";

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" centered>
      <ModalHeader toggle={toggle} className="bg-light px-4 py-3 border-bottom">
        <div className="d-flex align-items-center gap-3">
          <div
            className="avatar-sm rounded-circle bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center shadow-sm"
            style={{ width: "36px", height: "36px" }}
          >
            <i className="ri-history-line fs-18"></i>
          </div>
          <div>
            <h5 className="modal-title fw-bold text-dark mb-1 fs-16">
              {modalTitle}
            </h5>
            <div className="d-flex flex-wrap align-items-center gap-2">
              {subjectno && (
                <span className="badge bg-dark bg-opacity-10 text-dark border border-dark border-opacity-25 px-2.5 py-1 fs-12 fw-medium">
                  Subject No: <strong className="text-dark fw-bold">{subjectno}</strong>
                </span>
              )}
              {subjectbarcode && (
                <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-2.5 py-1 font-monospace fs-12 fw-medium">
                  Barcode: <strong className="text-primary fw-bold">{subjectbarcode}</strong>
                </span>
              )}
              {vaccutainerbarcode && type === "separation" && (
                <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 px-2.5 py-1 font-monospace fs-12 fw-medium">
                  Vacutainer: <strong className="text-dark fw-bold">{vaccutainerbarcode}</strong>
                </span>
              )}
              {aliquotbarcode && (
                <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 px-2.5 py-1 font-monospace fs-12 fw-medium">
                  Aliquot: <strong className="text-info fw-bold">{aliquotbarcode}</strong>
                </span>
              )}
              <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-2.5 py-1 fs-12 fw-medium">
                Total Scans: <strong className="text-success fw-bold">{totalCount}</strong>
              </span>
            </div>
          </div>
        </div>
      </ModalHeader>
      <ModalBody className="p-4 bg-white">
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner color="primary" size="md" />
            <div className="mt-2 text-muted fs-13 fw-medium">
              Fetching Attempt History...
            </div>
          </div>
        ) : (
          <div className="border rounded-3 bg-white overflow-hidden shadow-sm">
            <CommonTableIndexWise
              columns={columns}
              data={historyData}
              loading={isLoading}
              pageSize={pageSize}
              currentPage={currentPage}
              totalRecords={totalCount}
              onPageChange={setCurrentPage}
              actions={{}}
            />
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default TotalAttemptsHistoryModal;
